import { parseFromDecimal } from 'utils/parser.utils';

import {
  ContractDeposit,
  ContractWhitelistedDeposit,
  EarningsTableStatus,
  StakingPeriodDays,
  UserDeposit,
} from '../types';
import { PERIOD_LABELS } from '../constants';
import { isStuckVipDeposit } from '../overrides/stuck-vip-deposits';
import { toPeriodDaysEnum } from './period-helpers';
import {
  calculateDaysLeft,
  calculateDepositStatus,
  canClaimDeposit,
  canWithdrawDeposit,
} from './deposit-status';

export const timestampToDate = (timestamp: string | number): Date | null => {
  const ts = typeof timestamp === 'string' ? +timestamp : timestamp;
  return ts !== 0 ? new Date(ts * 1000) : null;
};

export const parseAmount = (amount: string, decimals: number): number => {
  return parseFloat(parseFromDecimal(amount, decimals));
};

export const formatApy = (apy: string | number): string => {
  return `${apy}%`;
};

export const parseApyFromWei = (apy: string, decimals: number): number => {
  return Math.round(parseFloat(parseFromDecimal(apy, decimals)));
};

interface MapPeriodicalDepositParams {
  result: ContractDeposit;
  index: number;
  tokenDecimals: number;
}

export const mapPeriodicalDeposit = ({
  result,
  index,
  tokenDecimals,
}: MapPeriodicalDepositParams): UserDeposit => {
  const status = calculateDepositStatus(result.withdrawalDate, result.stakingEndDate);
  const daysLeft = calculateDaysLeft(result.stakingEndDate);
  const periodDays = toPeriodDaysEnum(result.stakingPeriod);

  return {
    id: index,
    contractDepositIndex: index,
    phaseIndex: +result.stakingPhase,
    phaseName: `Phase ${+result.stakingPhase + 1}`,
    periodDays,
    periodLabel: PERIOD_LABELS[result.stakingPeriod] || `${result.stakingPeriod} Days`,
    stakingStartDate: new Date(+result.stakingStartDate * 1000),
    stakingEndDate: timestampToDate(result.stakingEndDate),
    withdrawalDate: timestampToDate(result.withdrawalDate),
    amount: parseAmount(result.amount, tokenDecimals),
    amountRaw: result.amount,
    apy: formatApy(result.APY),
    rewards: parseAmount(result.rewardGenerated, tokenDecimals),
    rewardsRaw: result.rewardGenerated,
    status,
    daysLeft,
    canClaim: canClaimDeposit(status, result.rewardGenerated),
    canWithdraw: canWithdrawDeposit(status, false),
    isWhitelisted: false,
    isVip: false,
  };
};

interface MapWhitelistedDepositParams {
  result: ContractWhitelistedDeposit;
  index: number;
  idOffset: number;
  tokenDecimals: number;
}

export const mapWhitelistedDeposit = ({
  result,
  index,
  idOffset,
  tokenDecimals,
}: MapWhitelistedDepositParams): UserDeposit => {
  const isWithdrawn = +result.withdrawalDate !== 0;
  const status = isWithdrawn ? EarningsTableStatus.WITHDRAWN : EarningsTableStatus.FLEXIBLE;
  const apyParsed = parseApyFromWei(result.APY, tokenDecimals);

  return {
    id: idOffset + index,
    contractDepositIndex: index,
    phaseIndex: 0,
    phaseName: 'Games Pool',
    periodDays: StakingPeriodDays.FLEXIBLE,
    periodLabel: 'Flexible',
    stakingStartDate: new Date(+result.stakingDate * 1000),
    stakingEndDate: null,
    withdrawalDate: timestampToDate(result.withdrawalDate),
    amount: parseAmount(result.amount, tokenDecimals),
    amountRaw: result.amount,
    apy: formatApy(apyParsed),
    rewards: parseAmount(result.claimableInterest, tokenDecimals),
    rewardsRaw: result.claimableInterest,
    status,
    daysLeft: null,
    canClaim: status === EarningsTableStatus.FLEXIBLE && +result.claimableInterest > 0,
    canWithdraw: status === EarningsTableStatus.FLEXIBLE,
    isWhitelisted: true,
    isVip: false,
    claimableInterest: result.claimableInterest,
    claimedInterest: result.claimedInterest,
  };
};

export const mapPeriodicalDeposits = (
  results: ContractDeposit[],
  tokenDecimals: number
): UserDeposit[] => {
  return results.map((result, index) =>
    mapPeriodicalDeposit({ result, index, tokenDecimals })
  );
};

export const mapWhitelistedDeposits = (
  results: ContractWhitelistedDeposit[],
  idOffset: number,
  tokenDecimals: number
): UserDeposit[] => {
  return results.map((result, index) =>
    mapWhitelistedDeposit({ result, index, idOffset, tokenDecimals })
  );
};

interface MapVipDepositParams {
  result: ContractDeposit;
  index: number;
  idOffset: number;
  tokenDecimals: number;
  walletAddress: string | null;
  networkId: string | number | null | undefined;
  vipContractAddress: string | null | undefined;
}

export const mapVipDeposit = ({
  result,
  index,
  idOffset,
  tokenDecimals,
  walletAddress,
  networkId,
  vipContractAddress,
}: MapVipDepositParams): UserDeposit => {
  const isStuck = isStuckVipDeposit(walletAddress, index, networkId, vipContractAddress);

  const status = isStuck
    ? EarningsTableStatus.CLAIMED
    : calculateDepositStatus(result.withdrawalDate, result.stakingEndDate);
  const daysLeft = isStuck ? null : calculateDaysLeft(result.stakingEndDate);
  const periodDays = toPeriodDaysEnum(result.stakingPeriod);

  return {
    id: idOffset + index,
    contractDepositIndex: index,
    phaseIndex: +result.stakingPhase,
    phaseName: 'VIP Staking',
    periodDays,
    periodLabel: PERIOD_LABELS[result.stakingPeriod] || `${result.stakingPeriod} Days`,
    stakingStartDate: new Date(+result.stakingStartDate * 1000),
    stakingEndDate: timestampToDate(result.stakingEndDate),
    withdrawalDate: timestampToDate(result.withdrawalDate),
    amount: parseAmount(result.amount, tokenDecimals),
    amountRaw: result.amount,
    apy: formatApy(result.APY),
    rewards: parseAmount(result.rewardGenerated, tokenDecimals),
    rewardsRaw: result.rewardGenerated,
    status,
    daysLeft,
    canClaim: !isStuck && canClaimDeposit(status, result.rewardGenerated),
    canWithdraw: !isStuck && canWithdrawDeposit(status, false),
    isWhitelisted: false,
    isVip: true,
  };
};

export const mapVipDeposits = (
  results: ContractDeposit[],
  idOffset: number,
  tokenDecimals: number,
  walletAddress: string | null,
  networkId: string | number | null | undefined,
  vipContractAddress: string | null | undefined
): UserDeposit[] => {
  return results.map((result, index) =>
    mapVipDeposit({ result, index, idOffset, tokenDecimals, walletAddress, networkId, vipContractAddress })
  );
};
